import { useEffect, useRef } from 'react'
import type { ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { cn } from '../lib/cn'
import { useHeldWhileClosing, usePresence } from '../lib/motion'
import { Button } from './Button'

/* خروجِ دیالوگ: در گوشی کشو ۲۰۰ میلی‌ثانیه پایین می‌رود (‎.pf-sheet‎ در tokens.css) */
const DIALOG_EXIT_MS = 200

/* ------------------------------------------------------------------
   Dialog — سند مادر بند ۱۱.۱
   ------------------------------------------------------------------
   الزامات دسترس‌پذیری:
   · focus trap و بازگرداندن فوکوس به عنصر فراخوان
   · بستن با Escape
   · aria-modal و aria-labelledby
   · اسکرول پس‌زمینه قفل می‌شود
   ------------------------------------------------------------------ */

export type DialogSize = 'sm' | 'md' | 'lg' | 'xl'

const SIZES: Record<DialogSize, string> = {
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
}

export interface DialogProps {
  open: boolean
  onClose: () => void
  title: ReactNode
  description?: ReactNode
  children?: ReactNode
  footer?: ReactNode
  size?: DialogSize
  /** بستن با کلیک بیرون — برای فرم‌های پرشده بهتر است false باشد */
  closeOnOverlayClick?: boolean
}

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'

export function Dialog({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
  closeOnOverlayClick = true,
}: DialogProps) {
  const panelRef = useRef<HTMLDivElement>(null)
  const previouslyFocused = useRef<HTMLElement | null>(null)

  /*
    🔴 `onClose` از راه ref خوانده می‌شود، نه از آرایهٔ وابستگیِ افکت.

    ⚠️ چرا: تقریباً همهٔ فراخوان‌ها تابعی درجا می‌دهند
    (`onClose={() => setOpen(false)}`) که در هر رندر هویتِ تازه دارد. اگر
    افکتِ زیر به آن وابسته باشد، هر تایپ در فرمِ داخلِ دیالوگ یک رندر
    می‌سازد، افکت پاک و دوباره اجرا می‌شود، پاک‌سازی فوکوس را به عنصرِ
    فراخوان برمی‌گرداند و اجرای تازه آن را روی نخستین عنصرِ پنل می‌نشاند —
    یعنی کاربر با هر حرف از فیلد بیرون می‌افتد.
  */
  const onCloseRef = useRef(onClose)
  useEffect(() => {
    onCloseRef.current = onClose
  }, [onClose])

  useEffect(() => {
    if (!open) return

    previouslyFocused.current = document.activeElement as HTMLElement | null

    const { overflow } = document.body.style
    document.body.style.overflow = 'hidden'

    // فوکوس اولیه روی نخستین عنصر قابل فوکوس داخل پنل
    const focusTimer = window.setTimeout(() => {
      const first = panelRef.current?.querySelector<HTMLElement>(FOCUSABLE)
      ;(first ?? panelRef.current)?.focus()
    }, 0)

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation()
        onCloseRef.current()
        return
      }

      if (event.key !== 'Tab') return

      const nodes = panelRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE)
      if (!nodes || nodes.length === 0) return

      const first = nodes[0]
      const last = nodes[nodes.length - 1]

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', onKeyDown, true)

    return () => {
      window.clearTimeout(focusTimer)
      document.removeEventListener('keydown', onKeyDown, true)
      document.body.style.overflow = overflow
      previouslyFocused.current?.focus()
    }
  }, [open])

  /*
    🔴 بسته شدن دیگر یعنی «همان لحظه برداشتن» نیست (lib/motion.ts): پنل
    DIALOG_EXIT_MS میلی‌ثانیه با data-state="closed" می‌ماند تا بیرون برود، با
    همان محتوای لحظهٔ آخرِ باز بودن، و در این مدت `inert` است — فوکوس و
    قفلِ اسکرول همان لحظهٔ بسته شدن (افکتِ بالا) آزاد شده‌اند.
  */
  const { mounted, state } = usePresence(open, DIALOG_EXIT_MS)
  const shown = useHeldWhileClosing({ title, description, children, footer }, open)

  if (!mounted) return null

  return createPortal(
    <div
      className="fixed inset-0 flex items-end justify-center p-0 sm:items-center sm:p-4"
      style={{ zIndex: 'var(--pf-z-dialog)' }}
      inert={!open}
    >
      {/* پرده از توکن می‌آید تا با بقیه پنجره‌های شناور یکی باشد. */}
      <div
        data-state={state}
        className="pf-scrim absolute inset-0"
        style={{
          backgroundColor:
            'color-mix(in srgb, var(--pf-scrim) var(--pf-scrim-opacity), transparent)',
          backdropFilter: 'blur(var(--pf-scrim-blur))',
          WebkitBackdropFilter: 'blur(var(--pf-scrim-blur))',
        }}
        onClick={closeOnOverlayClick ? onClose : undefined}
        aria-hidden="true"
      />

      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="pf-dialog-title"
        aria-describedby={shown.description ? 'pf-dialog-description' : undefined}
        tabIndex={-1}
        data-state={state}
        className={cn(
          'pf-sheet relative flex w-full flex-col overflow-hidden bg-[var(--pf-surface)]',
          'rounded-t-[var(--pf-radius-xl)] sm:rounded-[var(--pf-radius-xl)]',
          'shadow-[var(--pf-shadow-xl)] max-h-[92vh]',
          SIZES[size],
        )}
      >
        {/* 🔴 ۱۴۰۵/۰۸/۱۴ صاحبِ پروژه: در فارسی دکمهٔ بستن سمتِ راستِ پنجره است — پیش از عنوان، یعنی گوشهٔ راست */}
        <div className="flex items-start gap-3 border-b border-[var(--pf-border)] px-5 py-4">
          <button
            type="button"
            onClick={onClose}
            aria-label="بستن"
            className="-ms-1.5 flex size-8 shrink-0 items-center justify-center rounded-[var(--pf-radius)] text-[var(--pf-text-muted)] transition-colors hover:bg-[var(--pf-surface-hover)] hover:text-[var(--pf-text)]"
          >
            <svg viewBox="0 0 20 20" fill="none" className="size-4" aria-hidden="true">
              <path
                d="m5 5 10 10M15 5 5 15"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinecap="round"
              />
            </svg>
          </button>
          <div className="min-w-0 flex-1">
            <h2
              id="pf-dialog-title"
              className="text-[length:var(--pf-text-lg)] font-[var(--pf-weight-semibold)] text-[var(--pf-text)]"
            >
              {shown.title}
            </h2>
            {shown.description && (
              <p
                id="pf-dialog-description"
                className="mt-1 text-[length:var(--pf-text-sm)] text-[var(--pf-text-muted)]"
              >
                {shown.description}
              </p>
            )}
          </div>

        </div>

        {shown.children && <div className="flex-1 overflow-y-auto px-5 py-4">{shown.children}</div>}

        {shown.footer && (
          <div className="flex items-center justify-end gap-2 border-t border-[var(--pf-border)] px-5 py-3">
            {shown.footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  )
}

/* ------------------------------------------------------------------
   دیالوگ تأیید — برای اقدامات برگشت‌ناپذیر
   ------------------------------------------------------------------ */

export interface ConfirmDialogProps {
  open: boolean
  onClose: () => void
  onConfirm: () => void
  title: ReactNode
  description?: ReactNode
  confirmLabel?: string
  cancelLabel?: string
  tone?: 'primary' | 'danger'
  loading?: boolean
}

export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel = 'تأیید',
  cancelLabel = 'انصراف',
  tone = 'primary',
  loading = false,
}: ConfirmDialogProps) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      description={description}
      size="sm"
      closeOnOverlayClick={!loading}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={loading}>
            {cancelLabel}
          </Button>
          <Button variant={tone} onClick={onConfirm} loading={loading}>
            {confirmLabel}
          </Button>
        </>
      }
    />
  )
}
